import { Badge, Box, Button, Drawer, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { DialogCloseIcon, ExportIcon, FilterIcon, RefreshIcon } from '../../../assets/SvgIcons';
import { SearchIcon } from '../../customSVGs';
import CustomFilter from '../../investorOverview/common/CustomFilter';
import CustomTable from '../../investorOverview/common/customTable';
import { OrderHistoryTableBody } from './tables/body';
import { orderHistoryTableHeaderData } from './tables/headers';
import { OrderHistoryRes, OrderListObj, PaymentTypeFilter, ProductTypeFilter, TransactionTypeFilter } from '../../../redux-store/types/orderManagement';
import { getOrderHistoryList, getPaymentTypeFilter, getProductTypeFilter, getTransactionTypeFilter } from '../../../redux-store/actions/orderManagement';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '../../../utils/useDebounce';
import { RootStateType } from '../../../redux-store/reducers';
import { Order, PaginationObj, SortingObj } from '../../investorOverview/types';
import { whiteSpaceOnlyRegex } from '../../../utils/regex';
import { getAprovalStatusFilter } from '../../../redux-store/actions/orderApproval';
import { ApprovalStatuFilter } from '../../../redux-store/types/orderApproval';

export default function OrderHistory(): JSX.Element {
    const dispatch = useDispatch();
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const [productFilterData, setProductFilterData] = useState<ProductTypeFilter[]>([])
    const [transactionFilterData, setTransactionFilterData] = useState<TransactionTypeFilter[]>([])
    const [paymentFilterData, setPaymentFilterData] = useState<PaymentTypeFilter[]>([])
    const [orderFilterData, setOrderFilterData] = useState<ApprovalStatuFilter[]>([])
    const [searchText, setSearchText] = useState<string>('');
    const [orderHistoryLoading, setOrderHistoryLoading] = useState<boolean>(false);
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);
    const [orderHistoryTableData, setOrderHistoryTableData] = useState<OrderListObj[]>([])
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const theme = useTheme();
    const debounceSearchText = useDebounce(searchText, 500);

    const { iwappRmId = "" } = useSelector((store: RootStateType) => store.id)
    const isFilterApplied = productFilterData?.some(obj => obj.checked) || transactionFilterData?.some(obj => obj.checked) || paymentFilterData?.some(obj => obj.checked)


    const transactionFilterPayload = transactionFilterData?.filter(obj => obj.checked)?.map((obj: TransactionTypeFilter) => `${obj.orderStatusValue}`)

    const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map((obj: ProductTypeFilter) => `${obj.orderStatusValue}`)

    const paymentFilterPayload = paymentFilterData?.filter(obj => obj.checked === true)?.map((key: PaymentTypeFilter) => {
        if (key && key?.orderStatusId?.startsWith("paymentType-")) {
            return key?.orderStatusId?.substring("paymentType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const orderFilterPayload = orderFilterData?.filter(obj => obj.checked === true)?.map((key: ApprovalStatuFilter) => {
        if (key && key?.orderStatusId?.startsWith("orderType-")) {
            return key?.orderStatusId?.substring("orderType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true);
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const fetchOrderHistoryList = async ({
        sort = "",
        order = "",
        transactionType = [],
        productType = [],
        paymentStatus = [],
        approvalStatus = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: {
        sort?: string,
        order?: string,
        transactionType?: Array<string>,
        productType?: Array<string>,
        paymentStatus?: Array<string>,
        approvalStatus?: Array<string>,
        searchInput?: string,
        currentPage?: number,
        rowsPerPage?: number
    }) => {
        try {
            setOrderHistoryLoading(true);

            const payload = {
                rmId: `${iwappRmId}`,
                transactionType,
                productType,
                paymentStatus,
                approvalStatus,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage
            }

            const orderHistoryListRes = await (dispatch(getOrderHistoryList(payload)) as unknown as Promise<OrderHistoryRes>)

            const { orders = [], totalCount = 0 } = orderHistoryListRes;

            setOrderHistoryTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error', error);
        } finally {
            setOrderHistoryLoading(false);
        }
    }

    const handleApplyFilters = async () => {
        setFilterDrawerOpen(false)

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        const totalAppliedFilters =
            (transactionFilterPayload?.length ?? 0) +
            (productFilterPayload?.length ?? 0) +
            (paymentFilterPayload?.length ?? 0) +
            (orderFilterPayload?.length ?? 0);

        setTotalFilters(totalAppliedFilters)

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

    }

    const handleFiltersClearAll = async () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setTransactionFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setPaymentFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setOrderFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        setTotalFilters(0);

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

        setFilterDrawerOpen(false)
    }

    const handleProductTypeFilterChange = (value: string, checked: boolean) => {
        setProductFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }
    const handleTransactionTypeFilterChange = (value: string, checked: boolean) => {
        setTransactionFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }
    const handlePaymentTypeFilterChange = (value: string, checked: boolean) => {
        setPaymentFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleOrderTypeFilterChange = (value: string, checked: boolean) => {
        setOrderFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    const fetchFilterData = async () => {
        try {
            setIsFiltersLoading(true);
            const productFilterRes = await (dispatch(getProductTypeFilter()) as unknown as Promise<ProductTypeFilter[]>)
            setProductFilterData(productFilterRes?.map(obj => ({
                ...obj,
                orderStatusId: `productType-${obj.orderStatusId}`
            })));

            const transactionFilterRes = await (dispatch(getTransactionTypeFilter()) as unknown as Promise<TransactionTypeFilter[]>)
            setTransactionFilterData(transactionFilterRes?.map(obj => ({
                ...obj,
                orderStatusId: `transactionType-${obj.orderStatusId}`
            })));

            const paymentFilterRes = await (dispatch(getPaymentTypeFilter()) as unknown as Promise<PaymentTypeFilter[]>)
            setPaymentFilterData(paymentFilterRes?.map(obj => ({
                ...obj,
                orderStatusId: `paymentType-${obj.orderStatusId}`
            })))

            const orderFilterRes = await (dispatch(getAprovalStatusFilter()) as unknown as Promise<ApprovalStatuFilter[]>)
            setOrderFilterData(orderFilterRes?.map(obj => ({
                ...obj,
                orderStatusId: `orderType-${obj.orderStatusId}`
            })))
        } catch (e) {
            console.log('erorr', e);
        }
        finally {
            setIsFiltersLoading(false);
        }
    }

    const handleSort = async (orderBy: string, order: Order) => {

        setTableSortingObj({
            order,
            orderBy
        })

        fetchOrderHistoryList({
            sort: orderBy,
            order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: tablePaginationObj?.currentPage + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }


    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            searchInput: debounceSearchText.trim(),
            rowsPerPage: Number(event.target.value),
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleRefreshIconClick = async () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setTransactionFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setPaymentFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setOrderFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0,
            rowsPerPage: 10,
            totalRows: 0
        }))

        setTotalFilters(0);

        setSearchText("");

        setTableSortingObj({
            order: "desc",
            orderBy: ""
        })

        fetchOrderHistoryList({
            sort: "",
            order: "desc",
            transactionType: [],
            productType: [],
            paymentStatus: [],
            approvalStatus: [],
            searchInput: "",
            currentPage: 1,
            rowsPerPage: 10,
        })
    }

    useEffect(() => {
        fetchFilterData();
    }, [])

    useEffect(() => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }, [debounceSearchText])

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: "15.5px",
                    borderRadius: "25px",
                    padding: "20px 30px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Order History
                    </Typography>

                    <Box sx={{ display: 'flex', columnGap: '10px', alignItems: 'center' }}>

                        <IconButton
                            sx={{
                                cursor: 'pointer',
                            }}
                            onClick={handleRefreshIconClick}>
                            <RefreshIcon />
                        </IconButton>

                        {/* Filters */}
                        <Button
                            variant="text"
                            startIcon={
                                <Badge
                                    showZero
                                    badgeContent={totalFilters}
                                    sx={(theme: Theme) => ({
                                        '& .MuiBadge-badge': {
                                            right: 0,
                                            top: -3,
                                            backgroundColor: theme.palette.primary[500],
                                            color: theme.palette.common.white,
                                            ...theme.typography.subtitle_600
                                        },
                                    })}
                                >
                                    <FilterIcon />
                                </Badge>
                            }
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                padding: "5px",
                                ...theme.typography.body_500,
                                "&:hover": {
                                    backgroundColor: 'unset'
                                }
                            })}
                            TouchRippleProps={{
                                style: {
                                    color: theme.palette.grey[300]
                                }
                            }}
                            onClick={handleOpenFilterDrawer}
                        >
                            Filters
                        </Button>


                        {/* Export */}
                        <Box component={'button'} sx={{ cursor: 'pointer', border: 'none', display: 'flex', gap: '10px', background: 'transparent', alignItems: 'center' }}>
                            <ExportIcon sx={{ color: theme.palette.text.primary }} />
                            <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>Export</Typography>
                        </Box>

                        {/* Search Input */}
                        <TextField
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                            onClick={handleClearSearch} aria-label="Clear search"
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: (theme: Theme) => ({
                                    borderRadius: '57px',
                                    height: '47.17px',
                                    border: `1px solid ${theme.palette.text.secondary}B2`,
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '& input': {
                                        paddingLeft: '10px',
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            ...theme.typography.subtitle_400,
                                            color: theme.palette.text.secondary,
                                            opacity: 1,
                                        },
                                    },
                                }),
                            }}
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearchTextChange}
                        />
                    </Box>
                </Box>

                {/* Order History Table */}
                <Box sx={{ mt: '35px' }}>
                    <CustomTable
                        isLoading={orderHistoryLoading}
                        tableBody={
                            <OrderHistoryTableBody data={orderHistoryTableData ?? []} comingFrom='orderHistory' />
                        }
                        tableData={orderHistoryTableData ?? []}
                        tableHeaderData={orderHistoryTableHeaderData}
                        isPaginationVisible={true}
                        tableDataCount={tablePaginationObj?.totalRows ?? 0}
                        handleSort={handleSort}
                        order={tableSortingObj.order}
                        orderBy={tableSortingObj.orderBy}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        currentPage={tablePaginationObj.currentPage}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th': {
                                backgroundColor: '#EDF2F759',
                            },
                            '& th:first-of-type': {
                                paddingLeft: "0px",
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={5}
                    />
                </Box>
            </Box>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    showAmcFilter={false}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    showProductTypeFilter={true}
                    showPaymentTypeFilter={true}
                    showTransactionTypeFilter={true}
                    showApprovalTypeFilter={true}
                    productTypeFilterData={productFilterData}
                    transactionFilterData={transactionFilterData}
                    paymentFilterData={paymentFilterData}
                    approvalTypeFilterData={orderFilterData}
                    handleProductTypeFilterChange={handleProductTypeFilterChange}
                    handlePaymentTypeFilterChange={handlePaymentTypeFilterChange}
                    handleTransactionTypeFilterChange={handleTransactionTypeFilterChange}
                    handleApprovalTypeFilterChange={handleOrderTypeFilterChange}
                    isFilterApplied={isFilterApplied}
                    defaultExpandedFilter="productType"
                />
            </Drawer>
        </>

    )
}
